import colorConvert from 'color-convert';
import { useTranslation } from 'next-i18next';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import useMessaging from '../../../../../../hooks/useMessaging';
import { AttendeeMessage, MessagingConversation } from '../../../../../../interfaces/interfaces';
import { setMessagingOpenConversation } from '../../../../../../reduxToolkit/slices/commonSlice';
import { useSelector } from '../../../../../../reduxToolkit/typedRedux';
import AttendeeAvatar from '../../AttendeeAvatar';

interface ConversationListEntryProps {
  conversation: MessagingConversation;
  index: number;
}

export const ConversationListEntry = (props: ConversationListEntryProps) => {
  const { conversation, index } = props;

  const { t } = useTranslation();
  const { filterAndSortMessagesForConversation } = useMessaging();
  const messages = useSelector((state) => state.common.attendeeMessaging?.messages);

  const event = useSelector((state) => state.common.event);
  const user = useSelector((state) => state.common.user);
  const isMobilePreview = useSelector((state) => state.common.isMobilePreview);

  const dispatch = useDispatch();

  const [sortedMessages, setSortedMessages] = useState<AttendeeMessage[]>([]);

  if (!user) return null;

  /**
   * Find out if user A or userB is on the other end
   */
  const userKey = conversation.userA._id === user._id ? 'userB' : 'userA';
  const messagingAttendee = conversation[userKey];

  function handleConversationClick(bgColor?: string) {
    dispatch(setMessagingOpenConversation({ ...conversation, bgColor }));
  }

  const newMessagesCount = sortedMessages.filter((m) => m.sender === messagingAttendee._id && m.datetime_seen === null).length;

  const getLastMessage = (message?: AttendeeMessage, nameOfPartner?: string) => {
    if (!message) return;
    if (message.messageType === 'MESSAGE') {
      if (event?.settings.showLastSenderPrefix === true) {
        if (message.sender === user?._id) return `${t('common:you')}: ${message.message}`;
        else return `${nameOfPartner}: ${message.message}`;
      }
      return message.message;
    } else if (message.messageType === 'CALL') {
      if (message.datetime_ended) return t('meeting:on-site-call.call-ended');
      else return t('meeting:on-site-call.call');
    } else if (message.messageType === 'VCARD') return t('meeting:vcard-message-preview');
  };

  const eventColor = event?.template.mainColor;
  const getRandomColor = () => {
    if (!eventColor) return;
    let color = [216, 97, 34];
    const [h, s] = colorConvert.hex.hsl(eventColor);
    const randomToHundred = Math.floor(Math.random() * 50) + 30;
    color = [h, s, randomToHundred];
    return `hsl(${color[0]}, ${color[1]}%, ${color[2]}%)`;
  };
  const backgroundColor = getRandomColor();

  useEffect(() => {
    if (conversation) setSortedMessages(filterAndSortMessagesForConversation(conversation.userA._id, conversation.userB._id));
    else setSortedMessages([]);
  }, [conversation, messages]);

  return (
    <>
      <div
        key={conversation.conversationKey}
        className={`w-full px-2 py-2 flex justify-between items-center border-b-2 transition duration-200 ease-in-out bg-white hover:bg-slate-200 cursor-pointer overflow-hidden ${
          isMobilePreview ? '' : 'sm:flex-row'
        } `}
        onClick={() => handleConversationClick(backgroundColor)}>
        <div className="pr-2  h-full flex justify-center" onClick={(e) => e.stopPropagation()}>
          <AttendeeAvatar attendee={{ ...messagingAttendee, backgroundColor }} showStatus showStatusEmoji />
        </div>
        <div className="w-full flex flex-col">
          <div className="text-lg text-gray-900">
            {/* TODO: Construct username*/}
            <b>{messagingAttendee.name}</b>
          </div>
          <div className={`w-3/4 truncate text-md ${newMessagesCount === 0 ? 'text-slate-600' : 'text-slate-900 '}`}>
            {getLastMessage(sortedMessages.slice().shift(), messagingAttendee.name)}
          </div>
        </div>
        <div className="min-h-full grid place-items-center">
          {newMessagesCount > 0 && (
            <div
              style={{
                width: '30px',
                height: '30px',
                borderRadius: '50%',
                background: 'var(--scheme-main)',
                display: 'grid',
                placeItems: 'center',
              }}>
              <b className="text-white text-md">{newMessagesCount}</b>
            </div>
          )}
        </div>
      </div>
    </>
  );
};
